import { Component, ErrorHandler, Inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import { firstValueFrom } from 'rxjs'

import { DateService } from '@lemon/core/service/date.service'
import { Selvityspyynto } from '@lemon/core-firebase/model/selvitettavat'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'

export interface SelvityspyyntohistorianKirjausrivi {
  kuvaus: string
  selvityspyynto: Selvityspyynto
}

export interface KirjausSelvityspyyntohistoriaDialogData {
  kirjausrivit: SelvityspyyntohistorianKirjausrivi[]
}

interface SelvityspyyntohistorianRivi {
  vastattuMillis: number
  paiva: string
  kysymys: string
  vastaus: string
  vastaaja: string
  kirjausrivi: string
}

export const annaSelvityspyynnonVastatutKierrokset = (selvityspyynto: Selvityspyynto | null | undefined): Selvityspyynto[] => {
  if (!selvityspyynto) {
    return []
  }
  const kierrokset = [...(selvityspyynto.aikaisemmat ?? []), selvityspyynto]
  return kierrokset.filter(kierros => kierros.tila === 'vastattu')
}

@Component({
  templateUrl: './kirjaus.selvityspyyntohistoria.dialog.html',
  styleUrls: ['./kirjaus.selvityspyyntohistoria.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausSelvityspyyntohistoriaDialog implements OnInit {
  readonly rivit = signal<SelvityspyyntohistorianRivi[]>([])
  readonly naytaKirjausrivi = signal(false)

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: KirjausSelvityspyyntohistoriaDialogData,
    private _asiakasService: AsiakasService,
    private _dateService: DateService,
    private _errorHandler: ErrorHandler,
  ) {}

  async ngOnInit() {
    const kirjausrivitHistorialla = this._data.kirjausrivit.filter(kirjausrivi => annaSelvityspyynnonVastatutKierrokset(kirjausrivi.selvityspyynto).length > 0)
    this.naytaKirjausrivi.set(kirjausrivitHistorialla.length > 1)
    this.rivit.set(this._muodostaRivit(kirjausrivitHistorialla, new Map()))

    try {
      const kayttajat = await firstValueFrom(this._asiakasService.nykyisenAsiakkaanKayttajatObservable)
      const nimet = new Map((kayttajat ?? []).map(kayttaja => [kayttaja.avain, `${kayttaja.etunimi ?? ''} ${kayttaja.sukunimi ?? ''}`.trim()]))
      this.rivit.set(this._muodostaRivit(kirjausrivitHistorialla, nimet))
    } catch (error) {
      this._errorHandler.handleError(error)
    }
  }

  private _muodostaRivit(kirjausrivit: SelvityspyyntohistorianKirjausrivi[], nimet: Map<string, string>): SelvityspyyntohistorianRivi[] {
    const rivit: SelvityspyyntohistorianRivi[] = []
    for (const kirjausrivi of kirjausrivit) {
      for (const kierros of annaSelvityspyynnonVastatutKierrokset(kirjausrivi.selvityspyynto)) {
        rivit.push({
          vastattuMillis: kierros.vastattu?.toMillis() ?? 0,
          paiva: kierros.vastattu ? this._dateService.muotoilePaiva(kierros.vastattu, 'fi') : '',
          kysymys: kierros.kysymys ?? '',
          vastaus: kierros.vastaus ?? '',
          vastaaja: (kierros.vastaaja && nimet.get(kierros.vastaaja)) || kierros.vastaaja || '',
          kirjausrivi: kirjausrivi.kuvaus,
        })
      }
    }
    return rivit.sort((a, b) => b.vastattuMillis - a.vastattuMillis)
  }
}
